import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { getMyRole, adminAdInsights } from "@/lib/admin.functions";
import { categoryLabel } from "@/lib/categories";
import { formatPublished } from "@/lib/format-date";
import { formatBytes, formatSek } from "@/lib/ai-costs";


export const FIELD_LABELS: Record<string, string> = {
  title: "rubrik",
  description: "beskrivning",
  price: "pris",
  category: "kategori",
  location: "ort",
};

export type AdminInsights = Awaited<ReturnType<typeof adminAdInsights>>;

/** Delad hook: är inloggad användare admin? */
export function useIsAdmin() {
  const { user } = useAuth();
  const fetchRole = useServerFn(getMyRole);
  const { data } = useQuery({
    queryKey: ["my-role", user?.id],
    queryFn: () => fetchRole({}),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
  return !!data?.isAdmin;
}

/** Faktarader som delas av hover-bubblan och rutan på annonssidan. */
export function AdminAdFacts({ data }: { data: AdminInsights }) {
  // Äldre cachade svar (t.ex. från localStorage) kan sakna listorna – fall
  // tillbaka på tomma arrayer i stället för att krascha sidan.
  const tags = data.tags ?? [];
  const changedFields = data.changedFields ?? [];
  const matchedTags = data.matchedTags ?? [];
  const aiCalls = data.cost?.aiCalls ?? [];
  return (
    <div className="space-y-1">
      <p>
        <span className="text-muted-foreground">Upplagd:</span> {formatPublished(data.createdAt)}
      </p>
      <p>
        <span className="text-muted-foreground">Av:</span> {data.ownerName ?? "Okänd"}
        {data.ownerEmail ? ` (${data.ownerEmail})` : ""}
      </p>
      <p>
        <span className="text-muted-foreground">Ändrad:</span>{" "}
        {data.changeCount > 0 && data.lastChange
          ? `${data.changeCount} ggr, senast ${formatPublished(data.lastChange.created_at)} (${changedFields
              .map((f) => FIELD_LABELS[f] ?? f)
              .join(", ")})`
          : "aldrig"}
      </p>
      <p>
        <span className="text-muted-foreground">Kategori:</span>{" "}
        {categoryLabel(data.category ?? "ovrigt")}
      </p>
      <p>
        <span className="text-muted-foreground">Taggar:</span>{" "}
        {tags.length ? tags.join(", ") : "inga"}
      </p>
      {data.cost && (
        <>
          <p>
            <span className="text-muted-foreground">Lagring:</span>{" "}
            {formatBytes(data.cost.totalBytes)} ({data.cost.imageCount} bild
            {data.cost.imageCount === 1 ? "" : "er"} {formatBytes(data.cost.imageBytes)}
            {data.cost.videoBytes > 0 ? `, video ${formatBytes(data.cost.videoBytes)}` : ""})
          </p>
          <p>
            <span className="text-muted-foreground">AI-kostnad:</span>{" "}
            {formatSek(data.cost.aiTotalSek, 3)}
            {data.cost.aiEstimated ? " (uppskattad – mätdata saknas)" : " (uppmätt)"}
          </p>
          {aiCalls.length > 0 && (
            <ul className="ml-3 list-disc text-xs text-muted-foreground">
              {aiCalls.map((c) => (
                <li key={c.operation}>
                  {c.label}
                  {c.count > 1 ? ` ×${c.count}` : ""}: {formatSek(c.sek, 4)}
                  {c.tokens > 0 ? ` (${c.tokens.toLocaleString("sv-SE")} tokens)` : ""}
                </li>
              ))}
            </ul>
          )}
          <p>
            <span className="text-muted-foreground">Lagringskostnad:</span>{" "}
            {formatSek(data.cost.storageSek, 3)} / {data.cost.storageMonths} mån
          </p>
          <p className="font-semibold">
            <span className="text-muted-foreground">Total kostnad:</span>{" "}
            {formatSek(data.cost.totalSek, 3)}
          </p>
        </>
      )}

      {data.hasReference && (
        <p>
          <span className="text-muted-foreground">Matchar:</span>{" "}
          {matchedTags.length
            ? `taggar – ${matchedTags.join(", ")}`
            : data.matchesCategory
              ? "samma kategori"
              : "närhet"}
        </p>
      )}
      {(data.isPaused || data.soldAt || data.archivedAt) && (
        <p className="text-muted-foreground">
          Status:{" "}
          {[
            data.isPaused ? "pausad" : null,
            data.soldAt ? "såld" : null,
            data.archivedAt ? "arkiverad" : null,
          ]
            .filter(Boolean)
            .join(", ")}
        </p>
      )}
    </div>
  );
}

/** Ruta med admininfo längst ner på en annonssida (endast admin/superadmin). */
export function AdminAdInfoPanel({ slug }: { slug: string }) {
  const isAdmin = useIsAdmin();
  const fetchInsights = useServerFn(adminAdInsights);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-ad-insights", slug, null],
    queryFn: () => fetchInsights({ data: { slug, referenceSlug: null } }),
    enabled: isAdmin,
    staleTime: 60 * 1000,
  });

  if (!isAdmin) return null;

  return (
    <section className="mt-8 rounded-xl border border-border bg-card p-5 text-sm leading-relaxed text-card-foreground shadow-sm">
      <h2 className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
        Admininfo
      </h2>
      {isLoading || !data ? (
        <p className="mt-2 text-muted-foreground">Hämtar…</p>
      ) : (
        <div className="mt-2">
          <AdminAdFacts data={data} />
        </div>
      )}
    </section>
  );
}
